// =============================================================================
// Origin UI — Credit Card API Service
// Typed API service covering all corporate credit card endpoints.
// CC-9.1
// =============================================================================

import { api } from './apiService';

// ─── Types: Cards ───────────────────────────────────────────────────────────

export interface CorporateCardListItem {
  corporateCardId: string;
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  lastFourDigits: string;
  cardIssuer: string;
  cardCurrency: string;
  costCentreCode: string | null;
  creditLimit: number | null;
  isActive: boolean;
  cardNameOnStatement: string | null;
  approverName: string | null;
  approverEmployeeId: string | null;
}

export interface CorporateCard extends CorporateCardListItem {
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  approvers: CardApprover[];
}

export interface CreateCorporateCardDto {
  employeeId: string;
  lastFourDigits: string;
  cardIssuer: string;
  cardCurrency: string;
  costCentreCode?: string;
  creditLimit?: number;
  isActive?: boolean;
  cardNameOnStatement?: string;
}

export interface UpdateCorporateCardDto {
  lastFourDigits?: string;
  cardIssuer?: string;
  cardCurrency?: string;
  costCentreCode?: string;
  creditLimit?: number;
  isActive?: boolean;
  cardNameOnStatement?: string;
}

// ─── Types: Approvers ───────────────────────────────────────────────────────

export interface CardApprover {
  cardApproverId: string;
  corporateCardId: string;
  approverEmployeeId: string;
  approverName: string;
  cardholderName: string;
  lastFourDigits: string;
  cardIssuer: string;
}

export interface CreateCardApproverDto {
  corporateCardId: string;
  approverEmployeeId: string;
}

// ─── Types: Delegates ───────────────────────────────────────────────────────

export interface CreditCardDelegate {
  creditCardDelegateId: string;
  delegateUserId: string;
  delegateEmployeeId: string;
  delegateName: string;
  createdAt: string;
  createdBy: string | null;
}

export interface CreateCreditCardDelegateDto {
  delegateEmployeeId: string;
}

// ─── Types: Statement Periods ───────────────────────────────────────────────

export interface StatementPeriodListItem {
  statementPeriodId: string;
  periodName: string;
  periodYear: number;
  periodMonth: number;
  status: string;
  openedAt: string;
  launchedAt: string | null;
  closedAt: string | null;
  totalCards: number;
  pendingReviewCount: number;
  submittedCount: number;
  approvedCount: number;
  returnedCount: number;
}

export interface StatementPeriod extends StatementPeriodListItem {
  openedByUserId: string;
  closedByUserId: string | null;
  notes: string | null;
  uploads: StatementUpload[];
}

export interface CreateStatementPeriodDto {
  periodYear: number;
  periodMonth: number;
  notes?: string;
}

export interface StatementUpload {
  statementUploadId: string;
  statementPeriodId: string;
  fileName: string;
  cardIssuer: string;
  uploadedAt: string;
  uploadedByName: string;
  transactionCount: number;
  blobPath: string;
}

// ─── Types: Period Status (Admin detail view) ───────────────────────────────

export interface PeriodStatus {
  statementPeriodId: string;
  periodName: string;
  status: string;
  launchedAt: string | null;
  totalCards: number;
  pendingReviewCount: number;
  submittedCount: number;
  approvedCount: number;
  returnedCount: number;
  unmatchedTransactionCount: number;
  uploads: StatementUpload[];
  reviews: CardStatementReviewListItem[];
}

// ─── Types: Transactions ────────────────────────────────────────────────────

export interface CardTransactionListItem {
  cardTransactionId: string;
  statementPeriodId: string;
  corporateCardId: string | null;
  cardholderEmployeeId: string | null;
  transactionDate: string;
  postingDate: string;
  merchantName: string;
  merchantCategory: string | null;
  merchantCategoryCode: string | null;
  amount: number;
  currency: string;
  originalAmount: number | null;
  originalCurrency: string | null;
  cardLastFourDigits: string;
  cardholderNameOnStatement: string;
  commentary: string | null;
  commentaryUpdatedAt: string | null;
  commentaryUpdatedByName: string | null;
  isFlaggedPersonal: boolean;
  isFlaggedDisputed: boolean;
}

export interface CardTransaction extends CardTransactionListItem {
  statementUploadId: string;
  commentaryUpdatedByUserId: string | null;
  rawData: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCommentaryDto {
  commentary: string;
  isFlaggedPersonal: boolean;
  isFlaggedDisputed: boolean;
}

export interface BatchCommentaryItem {
  cardTransactionId: string;
  commentary: string;
  isFlaggedPersonal: boolean;
  isFlaggedDisputed: boolean;
}

export interface BatchUpdateCommentaryDto {
  items: BatchCommentaryItem[];
}

// ─── Types: Reviews ─────────────────────────────────────────────────────────

export interface CardStatementReviewListItem {
  cardStatementReviewId: string;
  statementPeriodId: string;
  corporateCardId: string;
  cardholderEmployeeId: string;
  cardholderName: string;
  approverEmployeeId: string;
  approverName: string;
  cardLastFourDigits: string;
  cardIssuer: string;
  status: string;
  transactionCount: number;
  totalAmount: number;
  reviewedTransactionCount: number;
  submittedAt: string | null;
  submittedByName: string | null;
  approvedAt: string | null;
  approverNotes: string | null;
  returnedAt: string | null;
  periodName: string;
  cardCurrency: string;
}

export interface CardStatementReview extends CardStatementReviewListItem {
  transactions: CardTransactionListItem[];
}

export interface ReturnReviewDto {
  notes: string;
}

// ─── Types: My Cards for Period ─────────────────────────────────────────────

export interface MyCardForPeriod {
  corporateCardId: string;
  lastFourDigits: string;
  cardIssuer: string;
  cardCurrency: string;
  reviewId: string | null;
  reviewStatus: string | null;
  transactionCount: number;
  reviewedTransactionCount: number;
  totalAmount: number;
}

// ─── Types: Import result ───────────────────────────────────────────────────

export interface ImportResult {
  statementUploadId: string;
  cardIssuer: string;
  fileName: string;
  totalTransactions: number;
  matchedTransactions: number;
  unmatchedTransactions: number;
  reviewsCreated: number;
  warnings: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// API Functions
// ═══════════════════════════════════════════════════════════════════════════

// ─── Cards ──────────────────────────────────────────────────────────────────

export const getCards = () =>
  api.get<CorporateCardListItem[]>('/api/corporate-cards');

export const getCard = (id: string) =>
  api.get<CorporateCard>(`/api/corporate-cards/${id}`);

export const createCard = (dto: CreateCorporateCardDto) =>
  api.post<CorporateCard>('/api/corporate-cards', dto);

export const updateCard = (id: string, dto: UpdateCorporateCardDto) =>
  api.put<CorporateCard>(`/api/corporate-cards/${id}`, dto);

export const deleteCard = (id: string) =>
  api.delete(`/api/corporate-cards/${id}`);

export const getCardsForEmployee = (employeeId: string) =>
  api.get<CorporateCardListItem[]>(`/api/employees/${employeeId}/cards`);

// ─── Approvers ──────────────────────────────────────────────────────────────

export const getApprovers = () =>
  api.get<CardApprover[]>('/api/card-approvers');

export const createApprover = (dto: CreateCardApproverDto) =>
  api.post<CardApprover>('/api/card-approvers', dto);

export const deleteApprover = (id: string) =>
  api.delete(`/api/card-approvers/${id}`);

// ─── Delegates ──────────────────────────────────────────────────────────────

export const getDelegates = () =>
  api.get<CreditCardDelegate[]>('/api/credit-card-delegates');

export const createDelegate = (dto: CreateCreditCardDelegateDto) =>
  api.post<CreditCardDelegate>('/api/credit-card-delegates', dto);

export const deleteDelegate = (id: string) =>
  api.delete(`/api/credit-card-delegates/${id}`);

// ─── Statement Periods ──────────────────────────────────────────────────────

export const getPeriods = () =>
  api.get<StatementPeriodListItem[]>('/api/statement-periods');

export const createPeriod = (dto: CreateStatementPeriodDto) =>
  api.post<StatementPeriod>('/api/statement-periods', dto);

export const getPeriodStatus = (id: string) =>
  api.get<PeriodStatus>(`/api/statement-periods/${id}/status`);

export const launchPeriod = (id: string) =>
  api.post<{ message: string }>(`/api/statement-periods/${id}/launch`, {});

export const closePeriod = (id: string) =>
  api.post(`/api/statement-periods/${id}/close`, {});

export const uploadStatement = (periodId: string, issuer: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.upload<ImportResult>(
    `/api/statement-periods/${periodId}/upload?issuer=${encodeURIComponent(issuer)}`,
    formData
  );
};

// ─── Transaction Review ─────────────────────────────────────────────────────

export const getMyTransactions = (
  periodId: string,
  cardId?: string,
  onBehalfOf?: string
) => {
  const params = new URLSearchParams();
  if (cardId) params.append('cardId', cardId);
  if (onBehalfOf) params.append('onBehalfOf', onBehalfOf);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return api.get<CardTransactionListItem[]>(
    `/api/statement-periods/${periodId}/my-transactions${qs}`
  );
};

export const getMyCardsForPeriod = (periodId: string, onBehalfOf?: string) => {
  const qs = onBehalfOf ? `?onBehalfOf=${encodeURIComponent(onBehalfOf)}` : '';
  return api.get<MyCardForPeriod[]>(
    `/api/statement-periods/${periodId}/my-cards${qs}`
  );
};

export const getMyReviewStatus = (
  periodId: string,
  cardId: string,
  onBehalfOf?: string
) => {
  const params = new URLSearchParams({ cardId });
  if (onBehalfOf) params.append('onBehalfOf', onBehalfOf);
  return api.get<CardStatementReviewListItem>(
    `/api/statement-periods/${periodId}/review-status?${params.toString()}`
  );
};

export const updateCommentary = (transactionId: string, dto: UpdateCommentaryDto) =>
  api.put(`/api/card-transactions/${transactionId}/commentary`, dto);

export const batchUpdateCommentary = (dto: BatchUpdateCommentaryDto) =>
  api.put('/api/card-transactions/batch-commentary', dto);

export const recallReview = (reviewId: string) =>
  api.post<CardStatementReview>(`/api/card-statement-reviews/${reviewId}/recall`, {});

export const submitReview = (reviewId: string) =>
  api.post(`/api/card-statement-reviews/${reviewId}/submit`, {});

// ─── Approvals ──────────────────────────────────────────────────────────────

export const getPendingApprovals = () =>
  api.get<CardStatementReviewListItem[]>('/api/card-statement-reviews/pending');

export const getReviewDetail = (id: string) =>
  api.get<CardStatementReview>(`/api/card-statement-reviews/${id}`);

export const approveReview = (id: string, notes?: string) =>
  api.post(`/api/card-statement-reviews/${id}/approve`, { notes: notes || null });

export const returnReview = (id: string, dto: ReturnReviewDto) =>
  api.post(`/api/card-statement-reviews/${id}/return`, dto);

// ─── Assign Unmatched Transaction ───────────────────────────────────────────

export const assignUnmatchedTransaction = (transactionId: string, corporateCardId: string) =>
  api.put(`/api/card-transactions/${transactionId}/assign`, { corporateCardId });
// ─── Receipt Types & API (append to end of creditCardApi.ts) ────────────────

export interface CardReceipt {
  cardTransactionReceiptId: string;
  cardTransactionId: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  uploadedAt: string;
  uploadedByName: string;
}

export const uploadReceipt = (transactionId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.upload<CardReceipt>(
    `/api/card-transactions/${transactionId}/receipts`,
    formData
  );
};

export const getReceipts = (transactionId: string) =>
  api.get<CardReceipt[]>(`/api/card-transactions/${transactionId}/receipts`);

export const downloadReceipt = (receiptId: string) =>
  `/api/card-transaction-receipts/${receiptId}/download`;

export const deleteReceipt = (receiptId: string) =>
  api.delete(`/api/card-transaction-receipts/${receiptId}`);
// ─── Unmatched Transactions (append to creditCardApi.ts) ─────────────────────

export interface UnmatchedGroup {
  cardholderName: string;
  lastFourDigits: string;
  transactionCount: number;
  totalAmount: number;
  currency: string;
  transactions: {
    cardTransactionId: string;
    transactionDate: string;
    merchantName: string;
    amount: number;
    currency: string;
  }[];
}

export const getUnmatchedTransactions = (periodId: string) =>
  api.get<UnmatchedGroup[]>(`/api/statement-periods/${periodId}/unmatched`);

export const bulkAssignUnmatched = (periodId: string, dto: {
  corporateCardId: string;
  cardholderNameOnStatement: string;
  lastFourDigits: string;
}) => api.put<{ message: string; assigned: number }>(
  `/api/statement-periods/${periodId}/assign-bulk`, dto
);
// ─── Personal Expenses (append to creditCardApi.ts) ─────────────────────────

export interface PersonalExpenseSummary {
  totalCount: number;
  totalAmount: number;
  byCurrency: { currency: string; count: number; total: number }[];
  transactions: PersonalExpenseItem[];
}

export interface PersonalExpenseItem {
  cardTransactionId: string;
  employeeName: string;
  employeeNumber: string;
  cardLastFourDigits: string;
  cardIssuer: string;
  transactionDate: string;
  merchantName: string;
  amount: number;
  currency: string;
  commentary: string | null;
  isFlaggedDisputed: boolean;
}

export const getPersonalExpenses = (periodId: string) =>
  api.get<PersonalExpenseSummary>(`/api/statement-periods/${periodId}/personal-expenses`);
// ─── CC Reports API (append to creditCardApi.ts) ────────────────────────────

export interface ReportTransaction {
  cardTransactionId: string;
  transactionDate: string;
  merchantName: string;
  merchantCategory: string;
  amount: number;
  currency: string;
  cardLastFourDigits: string;
  cardIssuer: string;
  cardholderName: string;
  employeeNumber: string;
  commentary: string | null;
  isFlaggedPersonal: boolean;
  isFlaggedDisputed: boolean;
  costCentre: string;
}

export interface ReportReview {
  corporateCardId: string;
  cardholderName: string;
  approverName: string;
  status: string;
  transactionCount: number;
  totalAmount: number;
  cardCurrency: string;
  cardIssuer: string;
  cardLastFour: string;
}

export interface ReportData {
  periodName: string;
  periodStatus: string;
  transactions: ReportTransaction[];
  reviews: ReportReview[];
}

export const getReportTransactions = (periodId: string) =>
  api.get<ReportData>('/api/credit-card-reports/' + periodId + '/transactions');
// ─── Delete Upload (append to creditCardApi.ts) ─────────────────────────────

export const deleteUpload = (uploadId: string) =>
  api.delete(`/api/statement-uploads/${uploadId}`);

// ─── Delegate: get employees I can act on behalf of ───
export interface DelegationTarget {
  employeeId: string;
  fullName: string;
  employeeNumber: string;
}

export const getMyDelegations = () =>
  api.get<DelegationTarget[]>('/api/credit-card-delegates/my-delegations');
